'use client';

import {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useAppDispatch } from '@/src/v2/hooks/useAppDispatch/UseAppDispatch';
import { useAppSelector } from '@/src/v2/hooks/useAppSelector/UseAppSelector';
import {
  setIsChatOpen,
  setFreeView,
  setHoldNames,
  setHoldNamesOnMouse,
  setConfMode,
  setIsChangeStream,
  setShowEventInfo,
  setReduxEventId,
  setIsShowAd,
} from '@/src/v2/store/slices/event/EventSlice';
import {
  initStreamMap,
  modifyStreamMap,
} from '@/src/v2/store/slices/streamMap/StreamMapSlice';
import {
  markAsInactive,
  markAsActive,
} from '@/src/v2/store/slices/inactiveMap/InactiveMapSlice';
import {
  IStatusTypeV2,
  IStreamersResponseV2,
  onEditEventChangeDocument,
  onEventActionChangeDocument,
  OnStartStreamerByAdminDocument,
  OnStopStreamerByAdminDocument,
} from '@/src/v2/service/v2/graphql/generated/gql-api.generated';
import { API } from 'aws-amplify';
import { isMobileInLandscape } from '@/src/v2/utils/helpers/device/Mobile';
import { StreamMapState } from '@/src/v2/store/slices/streamMap/StreamMapSlice';
import {
  addLockView,
  deleteLockView,
} from '@/src/v2/store/slices/lockView/LockViewSlice';

type IEventContext = {
  getStreamers: () => any[];
  streamersList: any[];
  updateStreamersList: (updatedStreamers: any[]) => void;
  controls: { [streamId: string]: any };
  layout: { prev: number | null; current: number | null };
  setLayout: (layout: { prev: number | null; current: number | null }) => void;
  setInitStreamMap: (
    streamers: (IStreamersResponseV2 & { isLocked: boolean })[],
    isLive: boolean,
    startingLayout: number,
    eventId: string,
    eventStatus?: IStatusTypeV2
  ) => void;
  updateStreamMap: (
    id: string,
    updateStreamMapPayload: Partial<{
      timestamp: number;
      isPin: boolean;
      stream: IStreamersResponseV2;
      volume: number;
    }>
  ) => void;
  getCurrentLayout: () => { id: string }[];
  transitionFromLayoutClick: (desiredLayout: number) => void;
  transitionFromToggle: (
    streamId: string,
    desiredLayout: number,
    isAlreadyActive: boolean
  ) => void;
  replaceStreamIdWithStreamId: (
    oldStreamId: string,
    newStreamId: string
  ) => void;
  newStreamIdWithStreamId: (
    oldStreamId: string,
    newStreamId: string,
    newIndex?: number
  ) => void;
  freeViewExpired: () => void;
  freeView: boolean;
  userHasPaid: () => void;
  inactiveMap: {
    [id: string]: {
      isActive: boolean;
    };
  };
  showEventInfo: boolean;
  holdNames: string;
  setHoldNames: (holdNames: string) => void;
  holdNamesOnMouse: boolean;
  setHoldNamesOnMouse: (holdNames: boolean) => void;
  setEventId: (eventId: string) => void;
  eventStatus: IStatusTypeV2;
  setEventStatus: (status: IStatusTypeV2) => void;
  eventId: string;
  setConfMode: (mode: boolean) => void;
  setIsChangeStream: (isChangeStream: string) => void;
  isChangeStream: string;
  streamMap: Record<
    string,
    {
      timestamp: number;
      id: string;
      isPin: boolean;
      stream: IStreamersResponseV2 & { isLocked: boolean };
      volume: number;
    }
  >;
  streamMapDispatch: (action: StreamMapAction) => void;
  setControls: (controls: any) => void;
  layoutMap: Record<string, { id: string }[]>;
  openChat: () => void;
  closeChat: () => void;
  LockView: (layoutNo: number) => void;
  isShowAd: boolean;
  setIsShowAd: (showAd: boolean) => void;
};

type StreamMapAction =
  | { type: 'init'; payload: { setup: StreamMapState } }
  | {
      type: 'modify';
      payload: { id: string; setup: Partial<StreamMapState[string]> };
    };

export const EventContext = createContext<IEventContext | undefined>(undefined);

export const useEventContext = () => {
  const context = useContext(EventContext);
  if (!context) {
    throw new Error(
      'useEventContext must be used within an EventContextProvider'
    );
  }
  return context;
};

export const EventContextProvider: FC<PropsWithChildren<{}>> = ({
  children,
}) => {
  const dispatch = useAppDispatch();
  const eventState = useAppSelector((state) => state.Event);
  const reduxUser = useAppSelector((state) => state.user.user);
  const inactiveMapState = useAppSelector((state) => state.InactiveMap);
  const lockViewState = useAppSelector((state) => state.LockView);

  const [eventId, setEventId] = useState<string>('');
  const [eventStatus, setEventStatus] = useState<IStatusTypeV2>(null);
  const [streamers, setStreamers] = useState<any[]>([]);
  const layoutRef = useRef({ prev: null, current: null });
  const [layoutMap, setLayoutMap] = useState<Record<string, { id: string }[]>>(
    {}
  );

  const setLayoutRef = (newLayout: {
    prev: number | null;
    current: number | null;
  }) => {
    layoutRef.current = newLayout;
  };

  const streamMapState = useAppSelector((state) => {
    const streamMap = state.StreamMap;

    const mappedStreamMap: Record<
      string,
      {
        timestamp: number | null;
        isPin: boolean;
        id: string;
        stream: IStreamersResponseV2 & { isLocked: boolean };
        volume: number;
      }
    > = {};

    for (const [key, value] of Object.entries(streamMap)) {
      mappedStreamMap[key] = {
        ...value,
        stream: {
          ...value.stream,
          isLocked: (value.stream as any).isLocked ?? false,
        },
        volume: value?.volume ?? 0,
      };
    }

    return mappedStreamMap;
  });

  const streamMapDispatch = (action: StreamMapAction) => {
    switch (action.type) {
      case 'init':
        dispatch(initStreamMap(action.payload.setup));
        break;
      case 'modify':
        dispatch(modifyStreamMap(action.payload));
        break;
      default:
        throw new Error(`Unknown action type`);
    }
  };

  const [controls, setControls] = useState({});
  const [layout, setLayout] = useState<{
    prev: number | null;
    current: number | null;
  }>({ prev: null, current: null });

  const updateStreamMap = (
    id: string,
    updateStreamMapPayload: Partial<{
      timestamp: number;
      isPin: boolean;
      stream: IStreamersResponseV2;
      volume: number;
    }>
  ) => {
    dispatch(modifyStreamMap({ id, setup: updateStreamMapPayload }));
  };

  const setInitStreamMap = (
    streamers: IStreamersResponseV2[],
    isLive: boolean,
    startingLayout: number,
    eventId: string,
    eventStatus?: IStatusTypeV2
  ) => {
    setEventId(eventId);
    // setEventStatus(eventStatus);
    const newStreamMap = streamers.reduce(
      (acc, stream) => {
        acc[stream.id] = {
          timestamp: isLive ? null : 0,
          isPin: false,
          id: stream.id,
          stream,
          volume: 0,
        };
        return acc;
      },
      {} as Record<
        string,
        {
          timestamp: number | null;
          isPin: boolean;
          id: string;
          stream: IStreamersResponseV2;
          volume: number;
        }
      >
    );
    dispatch(initStreamMap(newStreamMap));
    setLayoutRef({ prev: null, current: startingLayout });
    setup(null, startingLayout, newStreamMap);
  };

  const transitionFromLayoutClick = (desiredLayout: number) => {
    const previousLayout = layout.current;
    if (previousLayout !== desiredLayout) console.log(desiredLayout);
    {
      setLayoutRef({ prev: previousLayout, current: desiredLayout });
      setup(layoutRef.current.prev, desiredLayout);
    }
  };

  const transitionFromToggle = (
    streamId: string,
    desiredLayout: number,
    isActiveAlready: boolean
  ) => {
    const finalDesiredLayout = isMobileInLandscape()
      ? desiredLayout > 4
        ? 4
        : desiredLayout
      : desiredLayout > 9
      ? 9
      : desiredLayout;
    setLayoutRef({
      prev: layoutRef.current.current,
      current: finalDesiredLayout,
    });
    // add or remove
    const prevState = layoutMap[layoutRef.current.prev]?.map((i) => i.id) || [];
    let newLayoutMap;
    if (isActiveAlready) {
      newLayoutMap = {
        //...layoutMap,
        [finalDesiredLayout]: prevState
          .filter((i) => i !== streamId)
          .map((i) => ({ id: i })),
      };
    } else {
      newLayoutMap = {
        //...layoutMap,
        [finalDesiredLayout]: [
          { id: streamId },
          ...prevState.slice(0, finalDesiredLayout - 1).map((i) => ({ id: i })),
        ].slice(0, finalDesiredLayout),
      };
    }
    setLayoutMap(newLayoutMap);
  };
  const replaceStreamIdWithStreamId = (
    oldStreamId: string,
    newStreamId: string
  ) => {
    console.log(oldStreamId, newStreamId);
    console.log(layoutMap);

    // if both visible full replace
    const currentArray = layoutMap[layoutRef.current.current] || [];
    const updatedArray = [...currentArray];
    const oldObjIndex = currentArray.findIndex((obj) => obj.id === oldStreamId);
    const newObjIndex = currentArray.findIndex((obj) => obj.id === newStreamId);

    if (newObjIndex >= 0) {
      // both old and new objects exist in array
      // swap their positions
      const temp = currentArray[oldObjIndex];
      updatedArray[oldObjIndex] = currentArray[newObjIndex];
      updatedArray[newObjIndex] = temp;
    } else {
      const newObj = { id: newStreamId };
      updatedArray.splice(oldObjIndex, 1, newObj);
    }
    setLayoutMap({
      ...layoutMap,
      [layoutRef.current.current]: updatedArray,
    });
    setIsChangeStream(oldStreamId);
  };
  const tempsetup = (prev: number, desired: number, map = {}) => {
    if (
      (!Object.keys(streamMapState).length && !Object.keys(map).length) ||
      layoutRef.current.current === null
    ) {
      return;
    }
    const streamMapSetup = Object.keys(map).length ? map : streamMapState;
    const currentLayout = layoutMap[prev];
    if (!currentLayout) {
      let newLayoutMap = {
        ...layoutMap,
      };
      // take from prev
      if (prev !== null) {
        // check if we are adding or removing
        if (prev < desired) {
          // we are adding
          const previousStreamIds = layoutMap[prev];
          // filter current items
          const streamsForAdding = Object.keys(streamMapSetup).filter(
            (streamKey) => {
              return !previousStreamIds.some(
                (prevStreamKey) => prevStreamKey.id === streamKey
              );
            }
          );
          newLayoutMap[desired] = [
            ...newLayoutMap[prev],
            ...streamsForAdding.map((id) => ({ id })),
          ].slice(0, desired);
        } else if (newLayoutMap[prev]) {
          // removing
          newLayoutMap[desired] = newLayoutMap[prev].slice(0, desired);
        } else {
          newLayoutMap[desired] = Object.keys(streamMapSetup)
            .slice(0, desired)
            .map((streamId) => ({
              id: streamId,
            }));
        }
      } else {
        // just slice
        newLayoutMap[desired] = Object.keys(streamMapSetup)
          .slice(0, desired)
          .map((streamId) => ({
            id: streamId,
          }));
      }
      return newLayoutMap;
    } else {
      let newLayoutMap = {
        ...layoutMap,
      };
      newLayoutMap[desired] = Object.keys(streamMapSetup)
        .slice(0, desired)
        .map((streamId) => ({
          id: streamId,
        }));
      return newLayoutMap;
    }
  };
  const setup = (prev: number, desired: number, map = {}) => {
    if (
      (!Object.keys(streamMapState).length && !Object.keys(map).length) ||
      layoutRef.current.current === null
    ) {
      return;
    }
    const streamMapSetup = Object.keys(map).length ? map : streamMapState;
    const currentLayout = layoutMap[prev];
    if (!currentLayout) {
      let newLayoutMap = {
        ...layoutMap,
      };
      // take from prev
      if (prev !== null) {
        // check if we are adding or removing
        if (prev < desired) {
          // we are adding
          const previousStreamIds = layoutMap[prev];
          // filter current items
          const streamsForAdding = Object.keys(streamMapSetup).filter(
            (streamKey) => {
              return !previousStreamIds.some(
                (prevStreamKey) => prevStreamKey.id === streamKey
              );
            }
          );
          newLayoutMap[desired] = [
            ...newLayoutMap[prev],
            ...streamsForAdding.map((id) => ({ id })),
          ].slice(0, desired);
        } else if (newLayoutMap[prev]) {
          // removing
          newLayoutMap[desired] = newLayoutMap[prev].slice(0, desired);
        } else {
          newLayoutMap[desired] = Object.keys(streamMapSetup)
            .slice(0, desired)
            .map((streamId) => ({
              id: streamId,
            }));
        }
      } else {
        // just slice
        newLayoutMap[desired] = Object.keys(streamMapSetup)
          .slice(0, desired)
          .map((streamId) => ({
            id: streamId,
          }));
      }
      if (lockViewState[eventId]) {
        if (lockViewState[eventId][desired]) {
          const lockedLayout = lockViewState[eventId][desired].lockedLayout;
          const newLayoutMap: Record<string, { id: string }[]> = {
            [desired]: lockedLayout, // Use `lockedLayout` directly
          };
          setLayoutMap(newLayoutMap);
          return;
        }
      }
      setLayoutMap(newLayoutMap);
      return;
    } else {
      let newLayoutMap = {
        ...layoutMap,
      };
      newLayoutMap[desired] = Object.keys(streamMapSetup)
        .slice(0, desired)
        .map((streamId) => ({
          id: streamId,
        }));
      setLayoutMap(newLayoutMap);
    }
  };

  const LockView = (layoutNo: number) => {
    if (lockViewState[eventId]?.[layoutNo]) {
      dispatch(deleteLockView({ eventId, lockViewNumber: layoutNo }));
    } else {
      let setup;
      if (layoutMap[layoutNo]) {
        setup = {
          lockViewNumber: layoutNo,
          lockedLayout: layoutMap[layoutNo],
        };
      } else {
        const newMap = tempsetup(layoutRef.current.prev, layoutNo);
        setup = {
          lockViewNumber: layoutNo,
          lockedLayout: newMap[layoutNo],
        };
      }
      dispatch(addLockView({ eventId, setup }));
    }
  };

  const newStreamIdWithStreamId = (
    oldStreamId: string,
    newStreamId: string,
    newIndex?: number
  ) => {
    if (oldStreamId != newStreamId) {
      const currentArray = layoutMap[layoutRef.current.current];
      const updatedArray = [...currentArray];
      const oldObjIndex = currentArray.findIndex(
        (obj) => obj.id === oldStreamId
      );
      const newObjIndex = currentArray.findIndex(
        (obj) => obj.id === newStreamId
      );
      if (oldObjIndex != -1) {
        const newObj = { id: newStreamId };
        updatedArray.splice(oldObjIndex, 1, newObj);
        setLayoutMap({
          ...layoutMap,
          [layoutRef.current.current]: updatedArray,
        });
      }
      if (newObjIndex != -1) {
        updatedArray.splice(newObjIndex, 1, { id: oldStreamId });
      }
    }
  };
  const freeViewExpired = () => {
    setFreeView(true);
  };
  const userHasPaid = () => {
    setFreeView(false);
    setShowEventInfo(true);
  };

  const closeChat = () => {
    dispatch(setIsChatOpen(false));
  };

  const openChat = () => {
    dispatch(setIsChatOpen(true));
  };
  const getCurrentLayout = () => {
    return layoutMap[layoutRef.current.current] || [];
  };

  const updateStreamersList = (updatedStreamersList: any[]) => {
    setStreamers(updatedStreamersList);
  };

  const getStreamers = () => streamers;

  useEffect(() => {
    const stopStreamerObservable = API.graphql({
      query: OnStopStreamerByAdminDocument,
    }) as any;
    let stopStreamerObs = stopStreamerObservable.subscribe({
      next: (data: any) => {
        const payload = data.value.data.onStopStreamerByAdmin;
        dispatch(markAsInactive({ id: payload.id }));
      },
      error: (error) => {},
    });

    const startStreamerObservable = API.graphql({
      query: OnStartStreamerByAdminDocument,
    }) as any;
    let startStreamerObs = startStreamerObservable.subscribe({
      next: (data: any) => {
        const payload = data.value.data.onStartStreamerByAdmin;
        dispatch(markAsActive({ id: payload.id }));
      },
      error: (error) => {},
    });

    const onEventActionChangeObservable = API.graphql({
      query: onEventActionChangeDocument,
    }) as any;
    let onEventActionObs = onEventActionChangeObservable.subscribe({
      next: (data: any) => {
        if (
          data.value.data.onEventActionsV2 &&
          data.value.data.onEventActionsV2?.eventId === eventId
        ) {
          window.location.reload();
        } else if (
          data.value.data.onEventActionsV2 &&
          data.value.data.onEventActionsV2?.action
        ) {
          if (data.value.data.onEventActionsV2?.action === 'START') {
            setEventStatus(IStatusTypeV2.Live);
          } else if (data.value.data.onEventActionsV2?.action === 'END') {
            setEventStatus(IStatusTypeV2.Past);
          }
        }
      },
      error: (error) => {
        console.log('onEventActionObs ==> Error');
      },
    });
    const onEditEventChangeObservable = API.graphql({
      query: onEditEventChangeDocument,
    }) as any;
    let onEditEventObs = onEditEventChangeObservable.subscribe({
      next: (data: any) => {
        const payload = data.value.data.onEditEventV2;
        if (payload && payload.id === eventId && payload.is_change === true) {
          window.location.href = `/${payload.username}/event/${payload.slug}`;
        }
        if (payload && payload.id === eventId) {
          window.location.reload();
        }
      },
      error: (error) => {
        console.log('onEditEventObs ==> Error');
      },
    });
    return () => {
      stopStreamerObs.unsubscribe();
      startStreamerObs.unsubscribe();
      onEventActionObs.unsubscribe();
      onEditEventObs.unsubscribe();
    };
  }, [eventId]);
  return (
    <EventContext.Provider
      value={{
        getStreamers,
        streamersList: streamers,
        updateStreamersList,
        controls,
        layout: layoutRef.current,
        setLayout: setLayoutRef,
        setInitStreamMap,
        updateStreamMap,
        getCurrentLayout,
        transitionFromLayoutClick,
        transitionFromToggle,
        replaceStreamIdWithStreamId,
        newStreamIdWithStreamId,
        freeViewExpired,
        freeView: eventState.freeView,
        userHasPaid,
        layoutMap,
        inactiveMap: inactiveMapState,
        showEventInfo: eventState.showEventInfo,
        holdNames: eventState.holdNames,
        setHoldNames: (holdNames: string) => dispatch(setHoldNames(holdNames)),
        holdNamesOnMouse: eventState.holdNamesOnMouse,
        setHoldNamesOnMouse: (holdNamesOnMouse: boolean) =>
          dispatch(setHoldNamesOnMouse(holdNamesOnMouse)),
        setEventId: (eventId: string) => dispatch(setReduxEventId(eventId)),
        setConfMode: (mode: boolean) => dispatch(setConfMode(mode)),
        setIsChangeStream: (isChangeStream: string) =>
          dispatch(setIsChangeStream(isChangeStream)),
        isChangeStream: eventState.isChangeStream,
        eventId,
        streamMap: streamMapState,
        streamMapDispatch,
        setControls,
        openChat,
        closeChat,
        LockView,
        isShowAd: eventState.isShowAd,
        setIsShowAd: (showAd: boolean) => dispatch(setIsShowAd(showAd)),
        eventStatus,
        setEventStatus,
      }}
    >
      {children}
    </EventContext.Provider>
  );
};
